import { useQuery } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLocation } from "wouter";
import { api } from "../api";
import { useSession } from "../auth/session";
import { icons } from "../company-ui";
import { searchHelp } from "../help/content";
import { canOpen, hrefOf as pageHref, matchesPage, NAV_ITEMS, titleOf } from "./nav";

/** What the server finds (apps/server/src/routes/search.ts). */
interface ServerHit { kind: "program" | "lot" | "sku" | "pool" | "account" | "template" | "survey" | "activity"; id: string; label: string; sub: string; programId?: string | null; answer?: boolean }
interface Hit { group: string; key: string; label: string; sub: string; href: string }

/** Group order and headings, as in the mockup. Activities are the process map's (ALN-32). */
const GROUPS = ["Pages", "Programs", "Lots", "SKUs", "Pools", "Activities", "GL accounts", "Templates", "Surveys", "Help"] as const;
const GROUP_OF: Record<ServerHit["kind"], (typeof GROUPS)[number]> = {
  program: "Programs", lot: "Lots", sku: "SKUs", pool: "Pools", account: "GL accounts", template: "Templates", survey: "Surveys", activity: "Activities",
};
const enc = encodeURIComponent;

/** Where each kind of record opens. Screens read these query parameters when they are built. */
export function hrefOf(h: ServerHit): string {
  switch (h.kind) {
    case "program": return `/builder?program=${enc(h.id)}`;
    case "lot": return h.programId ? `/builder?program=${enc(h.programId)}&lot=${enc(h.label)}` : `/carry?lot=${enc(h.label)}`;
    case "sku": return `/wip?sku=${enc(h.label)}`;
    case "pool": return `/rates?pool=${enc(h.id)}`;
    case "account": return `/absorption?step=map&account=${enc(h.id)}`;
    case "template": return `/templates?template=${enc(h.id)}`;
    // a person who only answers opens the survey to answer it (Your surveys' Answer)
    case "survey": return h.answer ? `/survey?mine=${enc(h.id)}` : `/estimate?survey=${enc(h.id)}`;
    case "activity": return `/process?activity=${enc(h.id)}`;
  }
}

const PHONE = "(max-width: 760px)";
const isPhone = () => window.matchMedia(PHONE).matches;

export interface HeaderSearchHandle { focus(): void }

/**
 * Header search (ALN-53, mockup `.hsearch`). One box for pages, programs, lots, SKUs, pools, GL
 * accounts, templates, surveys and help. Records come from the tenant-scoped /api/search; pages and
 * help are matched here. `/` or Ctrl/Cmd+K focuses it, arrows move, Enter opens, Esc clears. At
 * phone widths it collapses to an icon that opens it full width.
 */
export const HeaderSearch = forwardRef<HeaderSearchHandle>(function HeaderSearch(_props, ref) {
  const { data: session } = useSession();
  const [, navigate] = useLocation();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);

  useImperativeHandle(ref, () => ({
    focus() {
      if (isPhone()) setPhoneOpen(true);
      // after a phone open renders the box
      requestAnimationFrame(() => { input.current?.focus(); input.current?.select(); });
    },
  }), []);

  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 120); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (phoneOpen) input.current?.focus(); }, [phoneOpen]);

  const server = useQuery({
    // Keyed by person as well as tenant: after a session ends, the next person never sees cached hits.
    queryKey: ["search", session?.tenant?.id, session?.user?.id, session?.role, debounced],
    queryFn: () => api<{ results: ServerHit[] }>(`/search?q=${enc(debounced)}`),
    enabled: debounced.length > 0,
    staleTime: 30_000,
  });

  const term = q.trim();
  const hits = useMemo<Hit[]>(() => {
    if (!term) return [];
    const words = term.toLowerCase().split(/\s+/);
    // Menu pages the person's roles can open (app-shell.md C), by the name each page goes by.
    // matched by menu label or page title, shown by the name the page goes by
    const pages = [...NAV_ITEMS.filter(i => canOpen(i, session) && matchesPage(i, term)).map(i => ({ label: i.tab ? i.label : titleOf(i), sub: i.tab ? "Compliance" : "", href: pageHref(i) })),
      ...(words.every(w => "your account".includes(w)) ? [{ label: "Your account", sub: "", href: "/account" }] : [])]
      .map(i => ({ group: "Pages", key: `page:${i.href}`, label: i.label, sub: i.sub, href: i.href }));
    // Only hits for the text in the box: never an earlier query's while the new one is pending.
    const records = server.data && debounced === term ? server.data.results.map(h => ({ group: GROUP_OF[h.kind], key: `${h.kind}:${h.id}`, label: h.label, sub: h.sub, href: hrefOf(h) })) : [];
    const help = searchHelp(term).slice(0, 4).map(a => ({ group: "Help", key: `help:${a.id}`, label: a.title, sub: a.lead, href: `/help/${a.id}` }));
    const all = [...pages.slice(0, 6), ...records, ...help];
    return GROUPS.flatMap(g => all.filter(h => h.group === g));
  }, [term, debounced, server.data, session]);

  useEffect(() => { setSel(0); }, [hits.length, term]);
  useEffect(() => { document.getElementById(`hs-${sel}`)?.scrollIntoView({ block: "nearest" }); }, [sel]);

  // Selecting outside the search closes its results (and the phone overlay).
  useEffect(() => {
    if (!open && !phoneOpen) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrap.current?.contains(t) && !opener.current?.contains(t)) { setOpen(false); setPhoneOpen(false); }
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open, phoneOpen]);

  const close = (clear: boolean) => {
    setOpen(false);
    if (clear) setQ("");
    if (phoneOpen) { setPhoneOpen(false); opener.current?.focus(); }
  };
  const pick = (h: Hit | undefined) => {
    if (!h) return;
    setOpen(false);
    setPhoneOpen(false);
    setQ("");
    input.current?.blur();
    navigate(h.href);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && hits.length) { e.preventDefault(); setOpen(true); setSel(s => (s + 1) % hits.length); return; }
    if (e.key === "ArrowUp" && hits.length) { e.preventDefault(); setOpen(true); setSel(s => (s - 1 + hits.length) % hits.length); return; }
    if (e.key === "Enter") { e.preventDefault(); pick(hits[sel]); return; }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (q) { setQ(""); setOpen(false); } else { close(false); if (!phoneOpen) input.current?.blur(); }
    }
  };

  const showPanel = open && term.length > 0;
  const settled = !server.isFetching && debounced === term;
  let n = -1;
  return (
    <>
      <div className={`hsearch${phoneOpen ? " hsearch--open" : ""}`} id="hsearch" role="search" ref={wrap}
        onBlur={e => {
          // Tabbing away closes the results (and the phone overlay); a tap elsewhere is the mousedown handler's.
          if (wrap.current?.contains(e.relatedTarget as Node)) return;
          setOpen(false);
          if (e.relatedTarget) setPhoneOpen(false);
        }}>
        <span className="hsearch__icon">{icons.search}</span>
        <label className="sr" htmlFor="gsearch">Search Avero Vintner</label>
        <input
          ref={input} id="gsearch" className="input hsearch__input" type="search" autoComplete="off" spellCheck={false}
          placeholder="Search programs, lots, SKUs, pools, accounts, help"
          role="combobox" aria-expanded={showPanel && hits.length > 0} aria-controls="gsearch-list" aria-autocomplete="list" aria-describedby="gsearch-kbd"
          aria-activedescendant={showPanel && hits.length ? `hs-${sel}` : undefined}
          value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
        />
        <kbd className="hsearch__kbd" id="gsearch-kbd"><span aria-hidden="true">/</span><span className="sr">Press slash to search</span></kbd>
        <div className="hsearch__panel" hidden={!showPanel}>
          <div id="gsearch-list" role="listbox" aria-label="Search results">
            {GROUPS.map(g => {
              const list = hits.filter(h => h.group === g);
              if (!list.length) return null;
              const gid = `hs-g-${g.replace(/\W+/g, "-").toLowerCase()}`;
              return (
                <div key={g} role="group" aria-labelledby={gid}>
                  <div className="hs__group" id={gid} role="presentation">{g}</div>
                  {list.map(h => {
                    const i = ++n;
                    return (
                      <div key={h.key} id={`hs-${i}`} className="hs__item" role="option" aria-selected={i === sel}
                        onMouseDown={e => e.preventDefault()} onMouseMove={() => setSel(i)} onClick={() => pick(h)}>
                        <b>{highlight(h.label, term)}</b>
                        {h.sub && <span className="hs__sub">{h.sub}</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {hits.length === 0 && (
            <div className="hs__empty" role="status">
              {settled ? <>Nothing matches "{term}". Try a lot code, SKU, account number or a word like "idle".</> : "Searching…"}
            </div>
          )}
          {server.isError && <div className="hs__empty" role="status">Records couldn't be searched just now. Pages and help are still listed.</div>}
        </div>
      </div>
      <button ref={opener} className="iconbtn hsearch__open" type="button" aria-label="Search" aria-controls="hsearch" aria-expanded={phoneOpen}
        onClick={() => setPhoneOpen(true)}>{icons.search}</button>
    </>
  );
});

/** The first place the query appears in the label, marked. */
function highlight(label: string, q: string): ReactNode {
  const i = label.toLowerCase().indexOf(q.toLowerCase());
  if (!q || i < 0) return label;
  return <>{label.slice(0, i)}<mark>{label.slice(i, i + q.length)}</mark>{label.slice(i + q.length)}</>;
}
